'use client';

import { Children, Fragment, isValidElement } from 'react';

interface SettingsRowGroupProps {
  children: React.ReactNode;
  /**
   * Tailwind inset for the inter-row divider, so it aligns with the row TITLE
   * (past the leading icon column). Default targets the render inspector's
   * `px-4` rows (16px gutter + 20px icon + 12px gap = 48px). The account page,
   * whose rows use the wider `px-5` gutter, passes `ms-[3.25rem] me-5` (52px).
   */
  dividerClassName?: string;
}

/**
 * Intersperses thin inset hairline dividers between a settings card's row
 * children — the iOS inset-grouped separator look (Apple `SettingsRowDivider`).
 *
 * The render settings sections each render a single `SettingsSectionRow` (plus
 * portaled drill-down sheets), but they live in separate components, so there
 * was no place to compose the inter-row separators — the grouped card read as
 * one undivided block. This primitive flattens the row children (fragments and
 * `cond && <Row/>` blocks collapse via `Children.toArray`) and drops a divider
 * between consecutive rows, so conditionally-hidden rows never leave a stray
 * leading/trailing line.
 *
 * The divider uses a tinted `muted-foreground` hairline (the `--border` token
 * is oklch(0.94) — nearly invisible on the light settings-card fill, which is
 * the exact bug this fixes) so it reads at the same weight as the native iOS
 * inset-grouped separator. It is inset on the leading side so it aligns with
 * the row TITLE, not the icon (override via `dividerClassName` per row gutter).
 */
export function SettingsRowGroup({
  children,
  dividerClassName = 'ms-12 me-4',
}: SettingsRowGroupProps) {
  const items = Children.toArray(children).filter(isValidElement);

  return (
    <>
      {items.map((child, index) => (
        <Fragment key={child.key ?? index}>
          {index > 0 && (
            <div
              className={`border-t border-muted-foreground/25 ${dividerClassName}`}
            />
          )}
          {child}
        </Fragment>
      ))}
    </>
  );
}
